import { useState, useEffect, useCallback } from 'react';
import { getTodayActivities, createActivity, updateActivity, deleteActivity } from '@/lib/firebase/queries/activities';
import type { Activity, CreateActivityParams, UpdateActivityParams } from '@/lib/firebase/queries/activities';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';

export function useActivities() {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const { currentUser } = useAuth();
  const { t } = useLanguage();

  const fetchActivities = useCallback(async () => {
    if (!currentUser?.uid) {
      setActivities([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const todayActivities = await getTodayActivities(currentUser.uid);
      setActivities(todayActivities);
    } catch (error) {
      console.error('Error fetching activities:', error);
      toast.error(t('activities.error.fetch') || 'Failed to fetch activities');
    } finally {
      setLoading(false);
    }
  }, [currentUser?.uid, t]);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  const addActivity = async (params: Omit<CreateActivityParams, 'userId'>) => {
    if (!currentUser?.uid) return null;

    try {
      const activityId = await createActivity({
        ...params,
        userId: currentUser.uid
      });
      await fetchActivities();
      toast.success(t('activities.success.created') || 'Activity logged successfully');
      return activityId;
    } catch (error) {
      console.error('Error creating activity:', error);
      toast.error(t('activities.error.create') || 'Failed to log activity');
      return null;
    }
  };

  const editActivity = async (activityId: string, params: UpdateActivityParams) => {
    try {
      await updateActivity(activityId, params);
      await fetchActivities();
      toast.success(t('activities.success.updated') || 'Activity updated successfully');
      return true;
    } catch (error) {
      console.error('Error updating activity:', error);
      toast.error(t('activities.error.update') || 'Failed to update activity');
      return false;
    }
  };

  const removeActivity = async (activityId: string) => {
    try {
      await deleteActivity(activityId);
      await fetchActivities();
      toast.success(t('activities.success.deleted') || 'Activity deleted successfully');
      return true;
    } catch (error) {
      console.error('Error deleting activity:', error);
      toast.error(t('activities.error.delete') || 'Failed to delete activity');
      return false;
    }
  };

  return {
    activities,
    loading,
    addActivity,
    editActivity,
    removeActivity,
    refreshActivities: fetchActivities
  };
}


